import { Grid } from '@mui/material'
import { Formik } from 'formik'
import { difference } from 'lodash'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toastNeg, toastPos } from '../../common/toastHelper'
import { IDropdownSkill, IEditedSkill, IEditedSkillInfo, ISkillFormPackage } from '../../interfaces/skills/ISkills'
import { useStore } from '../../Provider'
import { ExitConfirmation } from '../OPcomponents/ExitConfirmation'
import { OPCheckBox } from '../OPcomponents/OPCheckBox'
import { OPMultiline } from '../OPcomponents/OPMultiline'
import { OPSkillsCombobox } from '../OPcomponents/OPSkillsCombobox'
import { editSkillSchema } from '../OPcomponents/ValidationSchemas'
import { OPSkillsButtons } from '../Profile/OPSkillsButtons'

/*
TODO: selvitä miksi parent skill id ja eng desc ei näy
Mahdollista että parent skill idn voi ottaa pois (UI:ssa)
*/

const EditSkill = (props: { data: ISkillFormPackage; setSkillData: (val: ISkillFormPackage) => void }): JSX.Element => {
  const { t } = useTranslation()
  const { data, setSkillData } = props
  const rootStore = useStore()
  const { validatedUser, userPermission, isHr, isAdmin } = rootStore.loginStore

  const {
    currentSkill,
    dropdownCategories,
    dropdownTags,
    setShowEditInfo,
    showEditInfo,
    editSkillInfo,
    addSkillTag,
    removeSkillTag,
    setCurrentskill,
    allParentSkills,
  } = rootStore.skillStore

  // Guard: Ensure skill data is valid before rendering
  if (!data?.skill?.skill) {
    console.error('EditSkill: Invalid skill data received', data)
    return (
      <Grid container className='gridcontainer--formcontainer__itempaddings'>
        <Grid item xs={12}>
          <p>{t('Errors.SkillDataNotFound') || 'Skill data not found. Please try again.'}</p>
        </Grid>
      </Grid>
    )
  }

  const initialTags: IDropdownSkill[] = data.skill.skill.skillTags
    ? (data.skill.skill.skillTags
        .split(',')
        .map((tag: string) => tag.trim())
        .map((skillTag: string) => dropdownTags.find((tag) => tag.text === skillTag)) as IDropdownSkill[])
    : []

  const initialValues: IEditedSkill = {
    skillId: data.skill.skill.skillId,
    skillNameFi: data.skill.skill.skillNameFi,
    skillNameEng: data.skill.skill.skillNameEng,
    skillDescription: data.skill.skill.skillDescription ?? '',
    descriptionEng: data.skill.skill.descriptionEng ?? '',
    categoryId: data.skill.skill.skillCategoryId,
    skillTags: initialTags as IDropdownSkill[],
    isRequestedByCustomers: data.skill.skill.isRequestedByCustomers,
    // Not originally saved/used on submit in AddSkill either; added for UI parity
    parentSkillId: data.skill.skill.parentSkillId ?? 0,
  }

  const submitForm = async (values: IEditedSkill) => {
    // Guard: Only allow users with admin permissions to edit skill info
    if (!userPermission && !isHr && !isAdmin) {
      toastNeg(t('Toasts.NoPermission') || 'You do not have permission to edit skill information')
      setShowEditInfo(!showEditInfo)
      return
    }

    const editedSkill: IEditedSkillInfo = {
      nameFi: values.skillNameFi,
      nameEng: values.skillNameEng,
      description: values.skillDescription,
      descriptionEng: values.descriptionEng, // Assuming descriptionEng is the same as skillDescription
      categoryId: values.categoryId,
      parentSkillId: values.parentSkillId,
      creatorId: validatedUser,
      isRequestedByCustomers: values.isRequestedByCustomers,
    }
    // Note: parentSkillId is currently not persisted by editSkillInfo (parity with AddSkill create flow)
    const removedTags = difference(initialTags, values.skillTags)
    const addedTags = difference(values.skillTags, initialTags)

    try {
      await editSkillInfo(validatedUser, values.skillId, editedSkill, false)
      await Promise.all(
        addedTags
          .map((tag) => {
            addSkillTag(tag.key, values.skillId)
          })
          .concat(
            removedTags.map((tag) => {
              removeSkillTag(tag.key, values.skillId)
            }),
          ),
      )
      toastPos(t('Toasts.SkillInfoEdit'))
      setSkillData({
        ...data,
        skill: {
          ...data.skill,
          skill: {
            ...data.skill.skill,
            categoryId: values.categoryId,
            categoryName: dropdownCategories.find((category) => category.key === values.categoryId)?.text as string,
            skillCategoryId: values.categoryId,
            skillDescription: values.skillDescription,
            descriptionEng: values.descriptionEng,
            skillNameFi: values.skillNameFi,
            skillNameEng: values.skillNameEng,
            skillTags: values.skillTags.map((tag) => tag.text).join(', '),
            isRequestedByCustomers: values.isRequestedByCustomers ?? data.skill.skill.isRequestedByCustomers,
            parentSkillId: values.parentSkillId ?? data.skill.skill.parentSkillId,
          },
        },
      })
      setCurrentskill({
        ...currentSkill,
      })
    } catch (error) {
      toastNeg(t('Toasts.SkillInfoError'))
    } finally {
      setShowEditInfo(!showEditInfo)
    }
  }

  return (
    <Formik<IEditedSkill> initialValues={initialValues} onSubmit={submitForm} validationSchema={editSkillSchema}>
      {({ handleSubmit, handleChange, isValid, values, setFieldValue, dirty }) => (
        <Grid container rowSpacing={2} columnSpacing={4} className='gridcontainer--formcontainer__itempaddings'>
          <ExitConfirmation when={dirty} />
          {/* Category and Parent Skill (match AddSkill layout) */}
          <Grid item xs={12} lg={6}>
            <OPSkillsCombobox
              name='categoryId'
              optionList={dropdownCategories}
              curState={dropdownCategories.find((x) => x.key === values.categoryId)}
              label={`${t('Skills.Category')} *`}
              onChange={(_e, val) => {
                setFieldValue('categoryId', val.key)
              }}
              onDelete={() => {
                setFieldValue('categoryId', null)
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <OPSkillsCombobox
              name='parentSkillId'
              optionList={allParentSkills}
              curState={allParentSkills?.find((x) => x.key === values.parentSkillId)}
              disableClearable={false}
              label={t('Skills.ParentSkill')}
              onChange={(_e, val) => {
                setFieldValue('parentSkillId', val?.key ?? 0)
              }}
              onDelete={() => {
                setFieldValue('parentSkillId', null)
              }}
            />
          </Grid>

          {/* Names side by side */}
          <Grid item xs={12} lg={6}>
            <OPMultiline
              maxChars={100}
              hasCounter
              required
              name='skillNameFi'
              label={`${t('Skills.SkillFinnishName')}`}
              value={values.skillNameFi}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <OPMultiline
              maxChars={100}
              hasCounter
              name='skillNameEng'
              label={`${t('Skills.SkillEnglishName')}`}
              value={values.skillNameEng}
              onChange={handleChange}
            />
          </Grid>

          {/* Descriptions side by side */}
          <Grid item xs={12} lg={6}>
            <OPMultiline
              hasCounter
              maxChars={250}
              required
              name='skillDescription'
              label={`${t('Skills.SkillOverview')}`}
              value={values.skillDescription}
              onChange={handleChange}
              minRows={5}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <OPMultiline
              hasCounter
              maxChars={250}
              required
              name='descriptionEng'
              label={`${t('Skills.SkillOverviewEng')}`}
              value={values.descriptionEng}
              onChange={handleChange}
              minRows={5}
            />
          </Grid>

          <Grid xs={12} item>
            <OPCheckBox
              label={t('Skills.RequestedByCustomers')}
              value={!!values.isRequestedByCustomers}
              onChangeState={(_e, val) => setFieldValue('isRequestedByCustomers', val)}
            />
          </Grid>
          <Grid xs={12} lg={6} item>
            <OPSkillsCombobox
              multiple
              name='skillTags'
              optionList={dropdownTags}
              curState={values.skillTags}
              label={t('Skills.Tags')}
              onDelete={(val) => {
                setFieldValue(
                  'skillTags',
                  values.skillTags.filter((x) => x.key !== val.option.key),
                )
              }}
              onChange={(_e, val) => {
                setFieldValue('skillTags', val)
              }}
            />
          </Grid>
          <Grid xs={12} item>
            <OPSkillsButtons onClose={() => setShowEditInfo(!showEditInfo)} onSubmit={handleSubmit} disabled={!(isValid && dirty)} />
          </Grid>
        </Grid>
      )}
    </Formik>
  )
}

export default observer(EditSkill)
